<script setup lang="ts">
import { ref } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import Button from '@uiKit/elements/Button.vue'
import PiIcon from '@uiKit/elements/PiIcon.vue'
import SuccessTickAnimation from '@uiKit/components/SuccessTickAnimation.vue'
import { formatAed, formatToInternational } from '@shared/utils'

interface Props {
  propertyTitle: string
  tokens: number
  totalPayable: number
  orderReference: string
}

const props = defineProps<Props>()

defineEmits<{
  (e: 'view-receipt'): void
  (e: 'done'): void
}>()

const COPIED_RESET_MS = 2000

const copied = ref(false)
let copyResetTimer: ReturnType<typeof setTimeout> | undefined

async function copyReference() {
  try {
    await navigator.clipboard.writeText(props.orderReference)
    copied.value = true
    clearTimeout(copyResetTimer)
    copyResetTimer = setTimeout(() => (copied.value = false), COPIED_RESET_MS)
  } catch {
    // Clipboard unavailable (insecure context / permission denied) — no-op.
  }
}
</script>

<template>
  <Box direction="col" align="center" gap="6" class="invest-success__body">
    <!-- Ceremony -->
    <Box direction="col" align="center" gap="3">
      <SuccessTickAnimation />
      <Box direction="col" align="center" gap="2">
        <Text type="title-lg" weight="bold" align="center">Investment confirmed</Text>
        <Text type="body" color="muted" align="center" class="invest-success__subtitle">
          Funds debited from your wallet. Tokens will be minted once the property is fully funded.
        </Text>
      </Box>
    </Box>

    <!-- Receipt -->
    <Box direction="col" block class="invest-success__card">
      <Box direction="col" align="center" gap="1" class="invest-success__amount">
        <Text type="micro" color="muted">Debited from wallet</Text>
        <Text type="display" weight="bold" style="font-variant-numeric: tabular-nums">
          {{ formatAed(totalPayable) }}
        </Text>
      </Box>

      <Box class="invest-success__divider" />

      <Box direction="col" gap="3" class="invest-success__rows">
        <Box align="center" justify="between" gap="3" class="invest-success__row">
          <Text type="body-sm" is="span" color="muted">Property</Text>
          <Text type="body-sm" weight="semibold" is="span" align="right" :truncate="true">
            {{ propertyTitle }}
          </Text>
        </Box>
        <Box align="center" justify="between" gap="3" class="invest-success__row">
          <Text type="body-sm" is="span" color="muted">Tokens</Text>
          <Text type="body-sm" weight="semibold" is="span">{{ formatToInternational(tokens) }}</Text>
        </Box>
        <Box align="center" justify="between" gap="3" class="invest-success__row">
          <Text type="body-sm" is="span" color="muted">Order reference</Text>
          <Box align="center" gap="1" class="invest-success__reference">
            <Text type="body-sm" weight="semibold" is="span">{{ orderReference }}</Text>
            <Button
              :icon="copied ? 'check' : 'copy'"
              :color="copied ? 'success' : 'secondary'"
              variant="text"
              size="small"
              class="invest-success__copy"
              :aria-label="copied ? 'Copied' : 'Copy order reference'"
              @click="copyReference"
            />
          </Box>
        </Box>
      </Box>
    </Box>

    <!-- Trust: receipts are permanent -->
    <Box align="center" justify="center" gap="2" class="invest-success__note">
      <PiIcon name="shield" class="invest-success__note-icon" />
      <Text type="caption" color="muted" align="center">
        A copy of this receipt is saved to your orders.
      </Text>
    </Box>
  </Box>

  <Box direction="col" gap="2" class="invest-success__footer">
    <Button
      label="View receipt"
      icon="file"
      icon-pos="right"
      size="large"
      block
      @click="$emit('view-receipt')"
    />
    <Button label="Done" variant="outline" size="large" block @click="$emit('done')" />
  </Box>
</template>

<style scoped>
.invest-success__body {
  flex: 1;
  padding: 2.5rem 1.5rem 1.25rem;
}

.invest-success__subtitle {
  max-width: 32ch;
}

.invest-success__card {
  border: 1px solid var(--p-content-border-color);
  border-radius: var(--p-border-radius-lg);
  background: var(--p-surface-0);
  overflow: hidden;
}

.invest-success__amount {
  padding: 1.25rem 1rem;
  background: var(--p-surface-50);
}

.invest-success__divider {
  height: 1px;
  background: var(--p-content-border-color);
}

.invest-success__rows {
  padding: 1rem 1.125rem;
}

.invest-success__row,
.invest-success__reference {
  min-width: 0;
}

.invest-success__copy {
  flex-shrink: 0;
}

.invest-success__note-icon {
  font-size: 13px;
  color: var(--p-success-600);
}

.invest-success__footer {
  position: sticky;
  bottom: 0;
  padding: 0.75rem 1.5rem 1rem;
  background: var(--p-surface-0);
  border-top: 1px solid var(--p-content-border-color);
  box-shadow: 0 -8px 16px -16px rgba(0, 0, 0, 0.1);
}
</style>
